import { Suspense, useEffect, useState, type ReactNode } from 'react'
import { Route, Routes, useLocation, useNavigate, useParams, type Location } from 'react-router'
import NotFoundPage from '@/features/errors/pages/NotFoundPage'
import { isCommunitySlug } from '@/lib/reserved'
import { CommunityColumns } from './CommunityColumns'
import { CommunityLayout } from './CommunityLayout'
import { PageSkeleton } from './PageSkeleton'
import { backgroundFor } from './shell-utils'
import type { CommunityPageDef, LazyPageComponent, ModalRouteProps } from './types'

/** Lazy page with a Suspense fallback. Page comes from React.lazy at module level (the route table). */
export function LazyPage({ Page, fallback = null, ...props }: { Page: LazyPageComponent; fallback?: ReactNode } & Partial<ModalRouteProps>) {
  return (
    <Suspense fallback={fallback}>
      <Page {...props} />
    </Suspense>
  )
}

function columns(def: CommunityPageDef, element: ReactNode) {
  return (
    <CommunityColumns fullWidth={def.fullWidth} headerCard={def.headerCard}>
      <Suspense fallback={<PageSkeleton variant={def.fullWidth ? 'grid' : 'feed'} />}>{element}</Suspense>
    </CommunityColumns>
  )
}

type Opener = { el: HTMLElement; href: string | null }

function captureOpener(): Opener | null {
  const el = typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null)
  if (!el || el === document.body || el === document.documentElement) return null
  return { el, href: el.getAttribute('href') }
}

/** Gives focus back to the opener (or a fresh copy of the same link) unless something else took it. */
function restoreFocus(opener: Opener): void {
  const active = document.activeElement
  if (active && active !== document.body && active !== document.documentElement) return
  const target = opener.el.isConnected
    ? opener.el
    : opener.href
      ? document.querySelector<HTMLElement>(`a[href="${CSS.escape(opener.href)}"]`)
      : null
  target?.focus({ preventScroll: true })
}

/**
 * Remembers what had focus when a modal route opened and returns focus there after it closes
 * (Esc, X or Back), like a normal Radix dialog does for its trigger (Q-COMM-19, Q-SRCH-15).
 */
function useReturnFocus(active: boolean): void {
  const [opener] = useState(() => (active ? captureOpener() : null))
  useEffect(() => {
    if (!opener) return
    // Runs after Radix's own (delayed) focus restore, which lands on <body> when the opener was re-rendered.
    return () => void window.setTimeout(() => restoreFocus(opener), 30)
  }, [opener])
}

/** Post or community settings: a modal over the background page, or a full page on direct visits. */
function ModalRoute({ def, background }: { def: CommunityPageDef; background?: Location }) {
  const navigate = useNavigate()
  useReturnFocus(!!background)
  const onClose = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (background && idx > 0) navigate(-1)
    else if (background) navigate(background, { replace: true })
  }
  if (background) return <LazyPage Page={def.Page} variant="modal" onClose={onClose} />
  return columns(def, <LazyPage Page={def.Page} variant="page" onClose={onClose} />)
}

/** Every community page under /:slug/*. Reserved words and invalid slugs render the 404 page. */
export function CommunityRoutes({ pages }: { pages: CommunityPageDef[] }) {
  const { slug } = useParams()
  const location = useLocation()
  if (!slug || !isCommunitySlug(slug)) return <NotFoundPage />
  const background = backgroundFor(location, slug)
  const shown = background ?? location
  return (
    <CommunityLayout slug={slug} location={shown}>
      <Routes location={shown}>
        {pages.map((def) => {
          const element = def.modal ? <ModalRoute def={def} /> : columns(def, <LazyPage Page={def.Page} />)
          return def.path ? (
            <Route key={def.path} path={def.path} element={element} />
          ) : (
            <Route key="index" index element={element} />
          )
        })}
        <Route path="*" element={<CommunityColumns fullWidth notFound />} />
      </Routes>
      {background ? (
        <Routes>
          {pages
            .filter((def) => def.modal)
            .map((def) => (
              <Route key={def.path} path={def.path} element={<ModalRoute def={def} background={background} />} />
            ))}
        </Routes>
      ) : null}
    </CommunityLayout>
  )
}

/** Full screen live call page (no top bar). */
export function LiveRoute({ Page }: { Page: LazyPageComponent }) {
  const { slug } = useParams()
  if (!slug || !isCommunitySlug(slug)) return <NotFoundPage />
  return <LazyPage Page={Page} fallback={<PageSkeleton variant="blank" />} />
}
